/**
 * CoverLetterModal — cover letter generation modal.
 *
 * Checks preconditions, lets user pick length / tone / fidelity,
 * generates, then shows the result with copy button.
 * Settings are persisted across sessions via SettingsContext.
 */

import { useState } from 'react';
import { Mail, Loader2, Copy, Check, AlertCircle, CheckCircle2, RefreshCw, Eye } from 'lucide-react';
import { Modal } from '../../ui/Modal';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import type { CoverLetterLength, CoverLetterFidelity, CoverLetterTone } from '../../../lib/ai-types';

interface CoverLetterModalProps {
  open: boolean;
  onClose: () => void;
  activeTemplate: string;
  effectiveJobDescription: string;
  coverLetterLength: CoverLetterLength;
  setCoverLetterLength: (v: CoverLetterLength) => void;
  coverLetterFidelity: CoverLetterFidelity;
  setCoverLetterFidelity: (v: CoverLetterFidelity) => void;
  coverLetterTone: CoverLetterTone;
  setCoverLetterTone: (v: CoverLetterTone) => void;
  generatedCoverLetterByVersionId: Record<string, string>;
  isGeneratingCoverLetter: boolean;
  onGenerateCoverLetter: () => void | Promise<void>;
}

const LENGTH_OPTIONS: Array<{ value: CoverLetterLength; label: string; hint: string }> = [
  { value: 'flash',    label: 'Flash',    hint: '~100 mots max' },
  { value: 'short',   label: 'Court',    hint: '~150 mots' },
  { value: 'standard',label: 'Standard', hint: '~300 mots' },
  { value: 'long',    label: 'Long',     hint: '~500 mots' },
];

const TONE_OPTIONS: Array<{ value: CoverLetterTone; label: string; hint: string }> = [
  { value: 'direct',       label: 'Direct',       hint: 'Aller droit au but' },
  { value: 'professionnel',label: 'Professionnel', hint: 'Sérieux et posé' },
  { value: 'dynamique',    label: 'Dynamique',    hint: 'Énergique, actif' },
  { value: 'enthousiaste', label: 'Enthousiaste', hint: 'Chaleureux, engagé' },
];

const FIDELITY_OPTIONS: Array<{ value: CoverLetterFidelity; label: string; hint: string }> = [
  { value: 'fidele',  label: 'Fidèle', hint: 'Reste proche du CV' },
  { value: 'modere',  label: 'Équilibré', hint: 'Adapte sans exagérer' },
  { value: 'creatif', label: 'Créatif', hint: 'Reformulation large' },
];

export function CoverLetterModal({
  open,
  onClose,
  activeTemplate,
  effectiveJobDescription,
  coverLetterLength,
  setCoverLetterLength,
  coverLetterFidelity,
  setCoverLetterFidelity,
  coverLetterTone,
  setCoverLetterTone,
  generatedCoverLetterByVersionId,
  isGeneratingCoverLetter,
  onGenerateCoverLetter,
}: CoverLetterModalProps) {
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Une lettre peut exister en mémoire (auto-générée par le pipeline Auto-IA
  // ou par une session précédente). Ne pas la dévoiler à l'ouverture : on
  // cache la textarea derrière un « Afficher la lettre » pour que l'utilisateur
  // ne tombe pas dessus par erreur quand il voulait juste voir le CV.
  const storedLetter = generatedCoverLetterByVersionId[activeTemplate] || '';
  const [revealed, setRevealed] = useState(false);
  // React-recommended "storing information from previous renders" pattern:
  // https://react.dev/reference/react/useState#storing-information-from-previous-renders.
  // When the modal closes, the next open should start with the letter hidden.
  // Using useState (not useRef) keeps this inside React's render model and
  // satisfies react-hooks/refs + react-hooks/set-state-in-effect.
  const [prevOpen, setPrevOpen] = useState(open);
  if (prevOpen !== open) {
    setPrevOpen(open);
    if (!open && revealed) setRevealed(false);
  }
  const generatedLetter = revealed ? storedLetter : '';
  const hasJobOffer = !!effectiveJobDescription.trim();

  const handleGenerate = async () => {
    setError(null);
    try {
      await onGenerateCoverLetter();
      // Après génération explicite, dévoiler la lettre (l'utilisateur vient
      // de cliquer, il s'attend à voir le résultat).
      setRevealed(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur lors de la génération');
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedLetter);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  function OptionBtn<T extends string>({
    value,
    current,
    label,
    hint,
    onSelect,
  }: {
    value: T;
    current: T;
    label: string;
    hint: string;
    onSelect: (v: T) => void;
  }) {
    const active = value === current;
    return (
      <button
        type="button"
        onClick={() => onSelect(value)}
        className={`flex flex-col px-3 py-2 rounded-lg text-left transition-all ${
          active
            ? 'bg-violet-600/20 text-violet-300 border border-violet-500/30'
            : 'text-white/50 hover:text-white/70 hover:bg-white/5 border border-transparent'
        }`}
      >
        <span className="text-xs font-semibold">{label}</span>
        <span className="text-[10px] opacity-70 mt-0.5">{hint}</span>
      </button>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Lettre de motivation"
      size="xl"
    >
      <div className="space-y-5">
        {/* Precondition hint */}
        {!hasJobOffer && (
          <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-300 text-sm">
            <AlertCircle className="size-4 shrink-0 mt-0.5" />
            Sans offre d'emploi, la lettre sera générique. Pour un meilleur résultat, ajoute d'abord une offre via "Adapter".
          </div>
        )}
        {error && (
          <div className="flex items-start gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-300 text-sm">
            <AlertCircle className="size-4 shrink-0 mt-0.5" />
            {error}
          </div>
        )}

        {/* Config */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-white/50 uppercase tracking-wider">Longueur</Label>
            <div className="flex flex-col gap-1">
              {LENGTH_OPTIONS.map((opt) => (
                <OptionBtn
                  key={opt.value}
                  value={opt.value}
                  current={coverLetterLength}
                  label={opt.label}
                  hint={opt.hint}
                  onSelect={setCoverLetterLength}
                />
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-white/50 uppercase tracking-wider">Ton</Label>
            <div className="flex flex-col gap-1">
              {TONE_OPTIONS.map((opt) => (
                <OptionBtn
                  key={opt.value}
                  value={opt.value}
                  current={coverLetterTone}
                  label={opt.label}
                  hint={opt.hint}
                  onSelect={setCoverLetterTone}
                />
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-white/50 uppercase tracking-wider">Style</Label>
            <div className="flex flex-col gap-1">
              {FIDELITY_OPTIONS.map((opt) => (
                <OptionBtn
                  key={opt.value}
                  value={opt.value}
                  current={coverLetterFidelity}
                  label={opt.label}
                  hint={opt.hint}
                  onSelect={setCoverLetterFidelity}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Si une lettre existe déjà en mémoire mais pas encore affichée, on
            le signale sans la dévoiler : l'utilisateur choisit explicitement
            de la regarder (évite l'effet « j'ai ouvert la modale et pouf,
            la lettre s'est affichée d'elle-même »). */}
        {storedLetter && !revealed && (
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
            <p className="flex-1 text-xs text-emerald-200">
              <CheckCircle2 className="inline size-3.5 mr-1 -mt-0.5" />
              Une lettre existe déjà pour cette version du CV (sauvegardée avec le CV).
            </p>
            <button
              type="button"
              onClick={() => setRevealed(true)}
              className="shrink-0 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30 transition-colors"
            >
              <Eye className="size-3.5" /> Afficher la lettre
            </button>
          </div>
        )}

        {/* Bouton Générer — toujours visible. Si une lettre existe et n'est
            pas révélée, ce bouton écrasera la version existante → label clair. */}
        <button
          type="button"
          onClick={handleGenerate}
          disabled={isGeneratingCoverLetter}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold bg-violet-600 text-white hover:bg-violet-500 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-lg shadow-violet-600/20"
        >
          {isGeneratingCoverLetter ? (
            <><Loader2 className="size-4 animate-spin" />Génération en cours…</>
          ) : storedLetter ? (
            <><RefreshCw className="size-4" />Regénérer la lettre</>
          ) : (
            <><Mail className="size-4" />Générer la lettre</>
          )}
        </button>

        {/* Résultat affiché UNIQUEMENT après clic explicite (Générer ou Afficher). */}
        {generatedLetter && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-white/50 uppercase tracking-wider">Lettre générée</Label>
              <button type="button" onClick={handleCopy} className="flex items-center gap-1.5 text-xs text-white/50 hover:text-white/80 transition-colors">
                {copied ? <><Check className="size-3.5 text-emerald-400" />Copié</> : <><Copy className="size-3.5" />Copier</>}
              </button>
            </div>
            <Textarea
              value={generatedLetter}
              readOnly
              rows={12}
              className="bg-white/5 border-white/10 text-white/80 text-sm resize-none"
            />
            <p className="flex items-start gap-1.5 text-xs text-emerald-300/80 pt-1">
              <CheckCircle2 className="size-3.5 shrink-0 mt-0.5" />
              Lettre sauvegardée automatiquement avec cette version du CV. Vous pouvez fermer sans rien perdre.
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
}
